import { createClient } from '@supabase/supabase-js'

const supabaseUrl  = import.meta.env.VITE_SUPABASE_URL  as string
const supabaseAnon = import.meta.env.VITE_SUPABASE_ANON_KEY as string

if (!supabaseUrl || !supabaseAnon) {
  throw new Error('Missing Supabase environment variables. Copy .env.example to .env and fill in your credentials.')
}

// Full typed client will be regenerated from the real schema once the project is connected:
//   npx supabase gen types typescript --project-id YOUR_PROJECT_REF > src/types/database.ts
//
// auth.lock — deliberately NOT set (2026-07-02 idle-wake root-cause audit,
// docs/audits/IDLE-WAKE-DEADLOCK-ROOTCAUSE-2026-07-02.md). On auth-js 2.108.1
// a null lock is the good path: the client uses its lockless coordination
// (refresh single-flight + commit guard). PASSING a lock — even the old no-op
// `noLock` this file carried since 2026-05-20 — opts the client INTO the
// legacy in-process serialisation (`lockAcquired`/`pendingInLock`), and that
// path awaits `_notifyAllSubscribers` while HOLDING the lock: any subscriber
// that calls a lock-taking auth method (getSession etc.) inside
// onAuthStateChange deadlocks the whole client permanently, and every REST
// request starves before its fetch is issued (supabase-js resolves each
// bearer via `await auth.getSession()`). That was the root cause behind both
// idle-wake symptoms: the empty tenant switcher and the module-click
// forever-spinner.
//
// History: `lock: noLock` was the 2026-05-20 fix for navigator.locks
// contention — correct then, because pre-2.108 an omitted `lock` meant
// navigator.locks. On 2.108.1 omitting it means NO lock of any kind, so keep
// @supabase/supabase-js pinned (>=2.108 if it ever floats; on older auth-js
// omitting `lock` re-enables navigator.locks). Guardrail:
// scripts/check-auth-subscribers.mjs fails `npm test` if an
// onAuthStateChange callback lexically calls a lock-taking auth method, and
// keeps a conscious registry of subscriber files.

// ── Anon-fallback guard (2026-06-11) ────────────────────────────────────────
// supabase-js resolves every PostgREST request's bearer as
// `session?.access_token ?? anonKey`. When the access token is expired and the
// inline refresh fails TRANSIENTLY (device just woke, radio reattaching),
// getSession() returns null while a valid refresh_token still sits in
// localStorage — and every query silently goes out as `anon`. Under
// deny-by-default RLS that is not a 401: it's an HTTP 200 with EMPTY rows,
// which react-query records as success, renders as blank screens, and
// persists into the IndexedDB cache. This guard intercepts exactly that
// shape: a /rest/v1/ request carrying the anon key while a session exists on
// disk. It first asks the sessionWatchdog to refresh (single-flight,
// registered via setSessionRecoverer), retries with the healed token, and if
// the session is still unusable FAILS the request loudly — an error state the
// UI heals from (retry / reconnect / auth-heal invalidate), unlike a
// plausible-looking empty success.
type SessionRecoverer = () => Promise<void>
let sessionRecoverer: SessionRecoverer | null = null
export function setSessionRecoverer(fn: SessionRecoverer): void { sessionRecoverer = fn }

const AUTH_STORAGE_KEY = `sb-${new URL(supabaseUrl).host.split('.')[0]}-auth-token`

// Read the persisted session directly — deliberately NOT via offlineSession.ts
// (importing it here would create a module cycle: it imports this file).
function storedSession(): { access_token?: string; expires_at?: number; refresh_token?: string } | null {
  try {
    const raw = window.localStorage.getItem(AUTH_STORAGE_KEY)
    return raw ? JSON.parse(raw) : null
  } catch { return null }
}

// Exported for the getSession-wedge self-heal (getSessionHeal.ts), which uses it
// to confirm a live token sits on disk before deciding the in-process lock is
// wedged — the same direct-localStorage source of truth the anon-guard uses.
export function usableStoredToken(): string | null {
  const s = storedSession()
  if (!s?.access_token) return null
  // 10s margin: a token about to die mid-flight is as bad as a dead one.
  if (s.expires_at && s.expires_at * 1000 - Date.now() < 10_000) return null
  return s.access_token
}

// ── Global REST timeout net (finding H1, 2026-06-11) ────────────────────────
// fetch() has no default timeout; a black-holed TCP connection hangs for
// minutes at OS level. withTimeout/useSaveDb exist for exactly this, but 108
// of 119 module files writing through this shared client use neither — and
// hand-wrapping every call site is a week-scale sweep that can silently
// regress. This net bounds EVERY /rest/v1/ request instead: one change, all
// call sites. 30s mirrors SAVE_TIMEOUT_MS, so per-call withTimeout wrappers
// (with their friendlier messages + slow-save telemetry) still fire first
// where adopted; the net only catches what slipped through. Important
// semantics: aborting the WAIT does not abort the WORK — a timed-out write
// may still commit server-side, which is why SubmitOverlay's timeout copy
// says "check before retrying" (same caveat withTimeout always had). The
// abort surfaces as DOMException('TimeoutError'); main.tsx's retry policy
// skips retrying those (we already waited 30s — doubling the spinner on a
// black hole helps nobody). Composes with caller AbortSignals where the
// browser supports AbortSignal.any (all fleet browsers do).
const REST_TIMEOUT_MS = 30_000

// The few RPCs allowed to outlast the net, by name. generate_payroll_period
// rebuilds a whole pay run and measured 33.9 s for a 421-line month (Anchor,
// Oct 2026): at 30 s the net aborted it mid-chain, so the money steps after it
// never ran. Keep each value equal to its caller's withTimeout wait
// (GENERATE_PAYROLL_TIMEOUT_MS in saveTimeout.ts — not imported, because
// saveTimeout → uiTelemetry → this file; supabase.longRpc.test.ts pins them).
// ⚠️ The server still cancels at the role's statement_timeout (30 s for
// `authenticated`); this only stops the browser giving up first.
export const LONG_RPC_TIMEOUT_MS: Readonly<Record<string, number>> = {
  generate_payroll_period: 180_000,
  // The correction-run build and approve call generate_payroll_period inside,
  // then deductions and PAYE. Keep equal to GENERATE_PAYROLL_TIMEOUT_MS.
  refresh_correction_run: 180_000,
  approve_correction_run: 180_000,
}

/** The REST net's bound for this URL: a listed RPC's own, else REST_TIMEOUT_MS. */
export function restTimeoutFor(url: string): number {
  const m = /\/rest\/v1\/rpc\/([A-Za-z0-9_]+)/.exec(url)
  return (m && Object.prototype.hasOwnProperty.call(LONG_RPC_TIMEOUT_MS, m[1]) && LONG_RPC_TIMEOUT_MS[m[1]]) || REST_TIMEOUT_MS
}

// ── Auth-token timeout net (finding 2026-06-14) ─────────────────────────────
// The REST net above does NOT cover /auth/v1/. That gap is what lets the whole
// client stall: supabase-js resolves the bearer for EVERY request via
// `await auth.getSession()` (fetchWithAuth), and the refresh fetch auth-js
// runs has no timeout of its own. On today's lockless client (no `lock`
// option — see header) a black-holed refresh no longer wedges an in-process
// lock, but every caller still awaits the shared single-flight refresh
// (`refreshingDeferred`), so an unbounded refresh fetch would still starve
// all REST traffic for as long as the socket hangs. (Confirmed 2026-06-14,
// on the old lock path: a 23h-idle tab's tenant switch surfaced
// "set_preferred_tenant took too long" while the Network tab showed no
// calls at all and /auth/v1/health answered in ms — the network was fine, the
// client was stuck above it.) Bounding the auth fetch turns a hung refresh
// into a retryable error: the abort rejects the refresh, auth-js wraps a
// thrown fetch error as an AuthRetryableFetchError (session PRESERVED — never a
// sign-out, verified in auth-js _callRefreshToken: it skips _removeSession for
// retryable errors) and retries on a fresh socket.
//
// Recovery-latency caveat: this bounds each ATTEMPT, not the whole refresh.
// auth-js wraps _refreshAccessToken in its own `retryable` loop capped by
// AUTO_REFRESH_TICK_DURATION_MS (30s), so a black-hole now burns ~3 bounded
// attempts (10s + 200/400ms backoff) ~= 30s before the single-flight settles.
// REST calls that resolve their bearer via getSession() queue behind that loop,
// so they unwedge in ~30s, not 10s — the first action after a long sleep may
// still surface one "took too long". The win is bounded-not-permanent: no manual
// reload. The sessionWatchdog heals localStorage faster (~10s, its own raw fetch
// runs outside this loop), so subsequent reads pick up a fresh token promptly.
//
// 10s per attempt matches the codebase's "generous for a healthy auth round-trip"
// stance (the tenant-switch watchdog) and is far above p99 live-refresh latency,
// so a healthy-but-slow refresh is never aborted prematurely. (A smaller value
// wouldn't speed total recovery — the 30s ceiling is auth-js's, not ours.)
export const AUTH_TIMEOUT_MS = 10_000

// ── Storage timeout net (compliance-upload investigation, 2026-06-22) ────────
// supabase-storage-js's .upload() takes no AbortSignal (FileOptions has no
// `signal`), so a black-holed upload socket hangs at the OS level for minutes —
// and the isolated useSaveDb client, which is where doc uploads run, installed
// NO global fetch at all, so NOTHING bounded /storage/v1/. uploadWithTimeout's
// Promise.race only abandons the WAIT, never the request, so a timed-out upload
// kept saturating the uplink while the operator retried — the "uploads take
// forever, had to refresh" symptom. This is the hard backstop: a generous
// ceiling (above the 5-min per-file soft timeout in uploadPrep, so the friendly
// soft message always wins first on a merely-slow link) that actually tears the
// socket down on a true black hole, reclaiming bandwidth for the retry.
const STORAGE_TIMEOUT_MS = 6 * 60_000

// Bound a fetch with an AbortController so a black-holed TCP connection can't
// hang for minutes at the OS level (fetch has no default timeout). Aborts with
// an 'AbortError'-NAMED reason, not AbortSignal.timeout()'s 'TimeoutError':
// the installed postgrest-js (supabase-js 2.104 bundle) has an internal retry
// loop (DEFAULT_MAX_RETRIES=3) that only short-circuits on name==='AbortError'
// — a TimeoutError gets internally retried into a 2-minute spinner, defeating
// the point of the net; auth-js likewise wraps a thrown AbortError as a
// *retryable* fetch error (the behaviour we want). The message carries the
// timeout semantics for the react-query retry policy + SubmitOverlay copy.
// Composes with caller AbortSignals where the browser supports AbortSignal.any
// (all fleet browsers do).
function withAbortTimeout(init: RequestInit | undefined, ms: number, label: string): RequestInit | undefined {
  if (typeof AbortController === 'undefined') return init
  const ctrl = new AbortController()
  setTimeout(
    () => ctrl.abort(new DOMException(`Request timed out after ${ms / 1000}s (${label})`, 'AbortError')),
    ms,
  )
  const existing = init?.signal
  const signal = existing
    ? (typeof AbortSignal.any === 'function' ? AbortSignal.any([existing, ctrl.signal]) : existing)
    : ctrl.signal
  return { ...init, signal }
}

function withRestTimeout(init: RequestInit | undefined, url: string): RequestInit | undefined {
  return withAbortTimeout(init, restTimeoutFor(url), 'REST timeout net')
}

// ── Refresh-token adopt-not-replay (idle-wake audit H5, 2026-07-02) ─────────
// On a wake with a near-expired token, the watchdog's raw refresh POST and
// auth-js's own refresh can fire ~30ms apart carrying the SAME single-use
// refresh_token. GoTrue's ~10s reuse grace absorbs the first collision, but
// auth-js's retry loop replays the CAPTURED token per attempt without
// re-reading storage — a retry landing after the grace trips reuse-detection
// → token-family revoke → the freshly-healed session is wiped → forced
// re-login (the confirmed 2026-06-15 revoke incident). Because guardedFetch
// runs per ATTEMPT, rewriting the body to the freshest STORED refresh_token
// adopts the concurrent winner's rotation instead of replaying a consumed
// token. Best-effort: any parse surprise leaves the request untouched.
function adoptStoredRefreshToken(init: RequestInit | undefined): RequestInit | undefined {
  try {
    if (!init || typeof init.body !== 'string') return init
    const body = JSON.parse(init.body) as { refresh_token?: unknown }
    const stored = storedSession()?.refresh_token
    if (typeof body?.refresh_token !== 'string' || !stored || body.refresh_token === stored) return init
    return { ...init, body: JSON.stringify({ ...body, refresh_token: stored }) }
  } catch { return init }
}

// Exported for the anon-guard regression unit test (REL-005/006). It is the
// app's global fetch override (see createClient below); the test drives it
// directly to prove an anon-keyed REST read is healed-and-retried or blocked,
// never run as anon. (Pre-existing net — auth/REST timeout + anon-guard
// branches; merely exported here for the test, so its complexity is inherited.)
// fallow-ignore-next-line complexity
export const guardedFetch: typeof fetch = async (input, init) => {
  const url = typeof input === 'string' ? input : input instanceof URL ? input.href : input.url
  // Auth-token traffic (refresh/token, user, etc.) — bound it so a dead socket
  // can't hang the single-flight refresh every bearer awaits (see
  // AUTH_TIMEOUT_MS). No anon-guard here: these requests carry their own
  // apikey + refresh_token, not a session bearer, so the /rest/v1 "ran as
  // anon" shape below doesn't apply.
  if (url.includes('/auth/v1/')) {
    if (url.includes('grant_type=refresh_token')) init = adoptStoredRefreshToken(init)
    return fetch(input as RequestInfo, withAbortTimeout(init, AUTH_TIMEOUT_MS, 'auth timeout net'))
  }
  if (url.includes('/rest/v1/')) {
    init = withRestTimeout(init, url)
    const headers = new Headers(init?.headers ?? (input instanceof Request ? input.headers : undefined))
    const hasSession = !!storedSession()?.refresh_token
    if (hasSession && headers.get('Authorization') === `Bearer ${supabaseAnon}`) {
      // Belt-and-braces (2026-06-25): bound the recover await so this REST path
      // can NEVER be wedged by the recoverer, no matter what it does internally.
      // The recoverer is already bounded (authRefresh's fetch is abort-capped),
      // and its self-deadlock — it once awaited invalidateQueries(), which awaits
      // THIS very query's refetch — was just cut at the source (sessionWatchdog
      // fire-and-forgets the heal). This race is the structural guarantee so a
      // future change to the recover path can't silently re-introduce the
      // forever-spinner. On timeout we fall through to usableStoredToken(): if
      // the refresh already healed localStorage we proceed with the fresh token;
      // otherwise we fail loudly below rather than run as anon.
      if (sessionRecoverer) {
        await Promise.race([
          sessionRecoverer().catch(() => {}),
          new Promise<void>(r => setTimeout(r, AUTH_TIMEOUT_MS + 5_000)),
        ])
      }
      const token = usableStoredToken()
      if (!token) {
        // Logged-in user, no live token even after a recovery attempt. Fail
        // the request rather than let RLS answer "no rows" as a success.
        // Message shaped to match the transient-network swallow patterns in
        // errorReporter/Sentry — it IS transient; the heal paths refetch.
        //
        // AbortError-NAMED (2026-07-02, found by the §9 wedge e2e harness):
        // postgrest-js's internal retry loop only short-circuits on
        // name === 'AbortError' (same rationale as withAbortTimeout above). As
        // a TypeError this block was internally REPLAYED ~3×, each replay
        // re-running the full bounded auth chain — an operator staring at a
        // black-holed wake waited ~3 minutes for the visible error instead of
        // ~45s. Retrying the block inside postgrest is pointless by
        // construction: every replay re-enters this same guard.
        throw new DOMException('Failed to fetch (blocked: auth refresh pending, query would have run as anon)', 'AbortError')
      }
      headers.set('Authorization', `Bearer ${token}`)
      return fetch(input as RequestInfo, { ...init, headers })
    }
  }
  return fetch(input as RequestInfo, init)
}

export const supabase = createClient(supabaseUrl, supabaseAnon, {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
    // No `lock` — see the header comment. Passing ANY lock (even a no-op)
    // re-enables the legacy deadlock-prone in-process lock path.
  },
  global: { fetch: guardedFetch },
})

// Separate client for the public grower portal. Sends the share token as a
// header so the RLS policies on `runs` / `reject_log` / `qc_*` can gate access.
// No session persistence — every page load reads the token from the URL.
export function createGrowerClient(token: string) {
  return createClient(supabaseUrl, supabaseAnon, {
    auth: { persistSession: false, autoRefreshToken: false },
    global: { headers: { 'x-grower-token': token } },
  })
}

// Isolated client for long-running save mutations that need to avoid
// contending with the rest of the page for the main client's auth/refresh
// coordination. (Historically this dodged the auth-token locks — first
// navigator.locks, then the legacy in-process lock; the shared client is
// lockless since 2026-07-02, but a save that never awaits getSession still
// can't be stalled by a refresh in flight, so the isolation keeps earning
// its keep.) Use when:
//   - The user's QC modal does a multi-step save (insert qc_runs →
//     upload N photos → insert qc_responses) while the QC page is
//     ALSO running multiple useQuery hooks — on a slow tablet the shared
//     client's bearer resolution can queue the save behind a refresh.
//   - Any flow where we need predictable network behaviour without
//     the auto-refresh background activity interleaving with our work.
//
// persistSession: false + autoRefreshToken: false → the client does NOT
// touch localStorage and does NOT acquire the auth-token lock. We pass
// the JWT directly via the Authorization header at creation time, so
// every request just sends "Authorization: Bearer <jwt>" + apikey.
//
// Caller is responsible for sourcing a fresh JWT (e.g. from the main
// client's session at the start of the operation) and using this client
// only for the duration of that operation. Not a long-lived replacement
// for the main client.
export function createIsolatedClient(accessToken: string) {
  // The isolated client carries a baked-in bearer (no session on disk), so it
  // skips the anon-fallback guard / session-recoverer that guardedFetch runs
  // for the shared client. It does need a hard backstop on /storage/v1/ — doc
  // uploads run ONLY through this client and storage-js exposes no AbortSignal,
  // so without a net a dead upload socket hangs for OS-level minutes. We do NOT
  // net /rest/v1/ here: those writes are already bounded by per-call withTimeout
  // at the call sites (the DAL gate enforces it), and a fleet-wide REST abort
  // would be a broad behaviour change riding in on a storage fix.
  const isolatedFetch: typeof fetch = (input, init) => {
    const url = typeof input === 'string' ? input : input instanceof URL ? input.href : input.url
    if (url.includes('/storage/v1/')) {
      return fetch(input as RequestInfo, withAbortTimeout(init, STORAGE_TIMEOUT_MS, 'storage timeout net'))
    }
    return fetch(input as RequestInfo, init)
  }
  return createClient(supabaseUrl, supabaseAnon, {
    auth: { persistSession: false, autoRefreshToken: false },
    global: {
      headers: {
        Authorization: `Bearer ${accessToken}`,
        apikey:        supabaseAnon,
      },
      fetch: isolatedFetch,
    },
  })
}
